import React, { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { ChangeItem } from '../types'
import { getActionLabel, getChangeDetails, getLinkedItemType, DisplayNames } from '../utils/formatters'
import useDisplayNames from '../../../shared/hooks/useDisplayNames/useDisplayNames'
import useActivitiesDisplayName from '../../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import useEvidenceDisplayName from '../../../shared/hooks/useDisplayNames/variants/useEvidenceDisplayName'
import useValueMessageDisplayName from '../../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'

interface ChangeEntryProps {
    change: ChangeItem
}

const ChangeEntry = ({ change }: ChangeEntryProps) => {
    const { getDisplayName } = useDisplayNames()
    const valueMessage = useValueMessageDisplayName()
    const evidence = useEvidenceDisplayName()
    const activities = useActivitiesDisplayName()

    const displayNames = useMemo<DisplayNames>(
        () => ({
            valueMessages: valueMessage.plural,
            evidence: evidence.plural,
            activities: activities.plural,
            keyEvidenceStatements: getDisplayName('keyEvidenceStatements', 'Key Evidence Statements'),
        }),
        [getDisplayName, valueMessage.plural, evidence.plural, activities.plural]
    )

    const actionLabel = getActionLabel(change.action, change.entityType, change.metadata, change.newObj)
    const linkedItemType = getLinkedItemType(change, displayNames)
    const details = getChangeDetails(change)

    // Modify the action label to include the linked item type if applicable
    // For link/unlink actions, show both the row context and linked item type
    let displayLabel = actionLabel
    if (linkedItemType) {
        const actionVerb = actionLabel.split(':')[0] // e.g., "Linked" or "Unlinked"
        if (change.metadata?.rowName) {
            const subRowStr =
                change.metadata.subRowNumber && change.metadata.subRowNumber > 1
                    ? ` (Row ${change.metadata.subRowNumber})`
                    : ''
            displayLabel = `${actionVerb}: ${linkedItemType} in ${change.metadata.rowName}${subRowStr}`
        } else {
            displayLabel = `${actionVerb}: ${linkedItemType}`
        }
    }

    return (
        <Stack gap={1} pl={2}>
            {/* Action Label with colored bullet */}
            <Stack direction="row" alignItems="center" gap={1}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'figma.grayscale.stroke',
                        flexShrink: 0,
                    }}
                />
                <Typography variant="14x600" color="figma.grayscale.textHier1">
                    {displayLabel}
                </Typography>
            </Stack>

            {/* Change Details */}
            {details.items && details.items.length > 0 && (
                <Box component="ol" sx={{ m: 0, pl: 4, py: 0 }}>
                    {details.items.map((item, index) => (
                        <Box
                            component="li"
                            key={index}
                            sx={{
                                color: 'figma.grayscale.textHier2',
                                fontSize: '14px',
                                py: 0.25,
                            }}
                        >
                            <Typography variant="14x400" color="figma.grayscale.textHier2">
                                {item}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Old -> New value display for edits/deletes/creates */}
            {(details.oldValue !== undefined || details.newValue !== undefined) && (
                <Stack direction="row" gap={1} alignItems="center" pl={2} flexWrap="wrap">
                    {details.oldValue && (
                        <Typography variant="14x400" color="figma.grayscale.textHier2">
                            {details.oldValue}
                        </Typography>
                    )}
                    {details.oldValue && details.newValue && (
                        <Typography variant="14x400" sx={{ color: 'figma.primary' }}>
                            {'\u2192'}
                        </Typography>
                    )}
                    {details.newValue && (
                        <Typography variant="14x400" color="figma.grayscale.textHier1">
                            {details.newValue}
                        </Typography>
                    )}
                </Stack>
            )}

            {/* Reorder: Before/After lists */}
            {(details.reorderBefore || details.reorderAfter) && (
                <Stack gap={0.5} pl={2}>
                    {details.reorderBefore && details.reorderBefore.length > 0 && (
                        <Stack direction="row" gap={1} alignItems="flex-start">
                            <Typography variant="14x500" color="figma.grayscale.textHier2" sx={{ minWidth: '50px' }}>
                                Before:
                            </Typography>
                            <Typography variant="14x400" color="figma.grayscale.textHier2">
                                {details.reorderBefore.map((item, i) => `${i + 1}. ${item}`).join(', ')}
                            </Typography>
                        </Stack>
                    )}
                    {details.reorderAfter && details.reorderAfter.length > 0 && (
                        <Stack direction="row" gap={1} alignItems="flex-start">
                            <Typography variant="14x500" color="figma.grayscale.textHier1" sx={{ minWidth: '50px' }}>
                                After:
                            </Typography>
                            <Typography variant="14x400" color="figma.grayscale.textHier1">
                                {details.reorderAfter.map((item, i) => `${i + 1}. ${item}`).join(', ')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}

            {/* Cell content for actions without content diff */}
            {details.cellContent && (
                <Typography variant="14x400" color="figma.grayscale.textHier2" sx={{ pl: 2, fontStyle: 'italic' }}>
                    "{details.cellContent}"
                </Typography>
            )}
        </Stack>
    )
}

export default ChangeEntry
